import { ReactChild, useEffect, useRef, useState } from "react";

/*
showing / hiding is more complex when you add animation. the hidden state should be display-none for accessibility reasons, 
but css doesn't know how to animate out of that. so you need to add intermediate steps that css can animate.

shown <> visible <> invisible <> hidden

hidden (permanent state)
  display: none

invisible (transition state)
  display: block
  opacity: 0
  height: 0
  overflow: hidden
  transition: all 0.3s ease

visible (transition state)
  display: block
  opacity: 1
  height: ???px
  overflow: hidden
  transition: all 0.3s ease

shown (permanent state)
  display: block
*/

export const Expand = ({
    show,
    children,
    time = 350
}: {
    show: boolean;
    children: ReactChild;
    time?: number;
}) => {
    const startTimer: { current: ReturnType<typeof setTimeout> | null } =
        useRef(null);
    const endTimer: { current: ReturnType<typeof setTimeout> | null } =
        useRef(null);
    const container = useRef<HTMLDivElement>(null);

    const styles = {
        hidden: {
            display: "none"
        },
        invisible: {
            opacity: 0,
            overflow: "hidden",
            transition: `opacity ${time}ms, height ${time}ms`
        },
        visible: {
            opacity: 1,
            overflow: "hidden",
            transition: `opacity ${time}ms, height ${time}ms`
        },
        shown: {}
    };

    const [status, setStatus] = useState<{
        visibility: keyof typeof styles;
        height?: number;
    }>({
        visibility: show ? "shown" : "hidden",
        height: show ? container.current?.scrollHeight : 0
    });

    useEffect(() => {
        if (show === true) {
            if (status.visibility === "hidden") {
                setStatus({
                    visibility: "invisible",
                    height: 0
                });
            } else if (status.visibility === "invisible") {
                //get height
                setStatus({
                    visibility: "visible",
                    height: container.current?.scrollHeight
                });
                if (endTimer.current) {
                    clearTimeout(endTimer.current);
                }
                endTimer.current = setTimeout(() => {
                    setStatus({
                        visibility: "shown"
                    });
                }, time);
            }
        } else if (show === false) {
            if (status.visibility === "shown") {
                setStatus({
                    visibility: "visible",
                    height: container.current?.scrollHeight
                });
            } else if (status.visibility === "visible") {
                if (startTimer.current) {
                    clearTimeout(startTimer.current);
                }
                startTimer.current = setTimeout(() => {
                    setStatus({
                        visibility: "invisible",
                        height: 0
                    });
                }, 50);
                if (endTimer.current) {
                    clearTimeout(endTimer.current);
                }
                endTimer.current = setTimeout(() => {
                    setStatus({ visibility: "hidden" });
                }, time);
            }
        }
    }, [show, status, time]); //clean up
    useEffect(() => {
        return () => {
            if (startTimer.current) {
                clearTimeout(startTimer.current);
            }
            if (endTimer.current) {
                clearTimeout(endTimer.current);
            }
        };
    }, []);

    return (
        <div
            style={{ ...styles[status.visibility], height: status.height }}
            ref={container}
        >
            {children}
        </div>
    );
};
